import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ArrowLeft, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: password }),
      });

      const data = await response.json().catch(() => ({}));

      if (response.ok) {
        setSuccess(true);
        toast.success('Password reset successfully');
      } else {
        toast.error(data.detail || 'Reset failed. Please try again.');
      }
    } catch {
      toast.error('Could not reach server. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#1C1C1E] flex items-center justify-center p-6" data-testid="reset-no-token">
        <div className="w-full max-w-sm text-center">
          <h1 className="font-serif text-2xl text-[#F5F3EF] mb-3">Invalid Reset Link</h1>
          <p className="text-[#B5B1AB] font-sans text-sm mb-8">
            This link is invalid or has expired. Please request a new password reset.
          </p>
          <Link to="/forgot-password">
            <Button className="w-full bg-[#8BAA9E] hover:bg-[#7A9A8E] text-[#1C1C1E] rounded-full py-6 font-sans" data-testid="request-new-reset-btn">
              Request New Reset
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-[#1C1C1E] flex items-center justify-center p-6" data-testid="reset-success">
        <div className="w-full max-w-sm text-center">
          <div className="w-16 h-16 bg-[#8BAA9E]/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-8 h-8 text-[#8BAA9E]" />
          </div>
          <h1 className="font-serif text-2xl text-[#F5F3EF] mb-3">Password reset</h1>
          <p className="text-[#B5B1AB] font-sans text-sm mb-8">
            Your password has been updated. You can now sign in with your new password.
          </p>
          <Button
            onClick={() => navigate('/login')}
            className="w-full bg-[#8BAA9E] hover:bg-[#7A9A8E] text-[#1C1C1E] rounded-full py-6 font-sans"
            data-testid="go-to-login-btn"
          >
            Sign In
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#1C1C1E] flex items-center justify-center p-6" data-testid="reset-password-page">
      <div className="w-full max-w-sm">
        <Link to="/login" className="flex items-center gap-2 text-[#8BAA9E] text-sm font-sans mb-8 hover:text-[#A3C2B6] transition-colors">
          <ArrowLeft className="w-4 h-4" />
          Back to Sign In
        </Link>

        <h1 className="font-serif text-2xl text-[#F5F3EF] mb-2">Choose a new password</h1>
        <p className="text-[#B5B1AB] font-sans text-sm mb-8">
          Enter your new password below. Must be at least 6 characters.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            type="password"
            placeholder="New password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full bg-[#252528] border-white/10 rounded-xl py-6 px-4 text-[#F5F3EF] placeholder-[#7A7670] focus:border-[#8BAA9E]"
            data-testid="new-password-input"
            required
            minLength={6}
          />
          <Input
            type="password"
            placeholder="Confirm new password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className="w-full bg-[#252528] border-white/10 rounded-xl py-6 px-4 text-[#F5F3EF] placeholder-[#7A7670] focus:border-[#8BAA9E]"
            data-testid="confirm-password-input"
            required
            minLength={6}
          />
          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-[#8BAA9E] hover:bg-[#7A9A8E] text-[#1C1C1E] rounded-full py-6 font-sans disabled:opacity-50"
            data-testid="reset-password-btn"
          >
            {loading ? 'Resetting...' : 'Reset Password'}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default ResetPassword;
